import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    IconButton,
    Tooltip,
    Fade,
    Tabs,
    Tab,
    Stack,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Badge,
} from '@mui/material';
import {
    SportsEsports as SportsEsportsIcon,
    Deck as DeckIcon,
    Add as AddIcon,
    Assessment as AssessmentIcon,
    Palette as PaletteIcon,
    CheckBoxOutlined as CheckBoxOutlinedIcon,
    CheckBox as CheckBoxIcon,
    Person as PersonIcon,
    BarChart as BarChartIcon,
    LocalFireDepartment as LocalFireDepartmentIcon,
    SyncAlt as SyncAltIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getTrackerDashboardData, deleteMatches, getExpansionSets, getPreferredTrackerSetId, normalizeTrackerSetId, TRACKER_DEFAULT_SET_ID, updateMatch, updateUserTrackerContext } from '../../services/tracker-data';
import { getDuelsSyncStatus } from '../../services/duels-sync-service';
import QuickMatchForm from './QuickMatchForm';
import QuickMatchMobile from './QuickMatchMobile';
import DeckManager from './DeckManager';
import ArchetypeManager from './ArchetypeManager';
import StatisticsPanel from './StatisticsPanel';
import UserStatsPanel from './UserStatsPanel';
import MatchesList from './MatchesList';
import ConfirmDialog from './ConfirmDialog';
import DuelSyncPendingQueue from './DuelSyncPendingQueue';
import DuelSyncImportWizard from './DuelSyncImportWizard';

const TRACKER_CONTEXT_STORAGE_KEY = 'tracker:selectedContext';
const FORMAT_LABELS = {
    core_constructed: 'Core Constructed',
    infinity: 'Infinity'
};
const isValidTrackerFormat = (value) => value === 'core_constructed' || value === 'infinity';

function Tracker() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentUser, userData, refreshUserData, loading: authLoading } = useAuth();
    const [activeSection, setActiveSection] = useState('overview');
    const [quickMatchOpen, setQuickMatchOpen] = useState(false);
    const [showMobileMatchForm, setShowMobileMatchForm] = useState(false);
    const [preselectedDeckId, setPreselectedDeckId] = useState(null);
    const [editMatch, setEditMatch] = useState(null);
    const [archetypeDialogOpen, setArchetypeDialogOpen] = useState(false);
    const [deckDialogOpen, setDeckDialogOpen] = useState(false);
    const [deleteMode, setDeleteMode] = useState(false);
    const [selectedMatches, setSelectedMatches] = useState(new Set());
    const [stats, setStats] = useState({
        totalMatches: 0,
        totalDecks: 0,
        winrate: 0,
        wins: 0,
        losses: 0,
        draws: 0
    });
    const [loading, setLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedFormat, setSelectedFormat] = useState('core_constructed');
    const [selectedSetId, setSelectedSetId] = useState(TRACKER_DEFAULT_SET_ID);
    const [expansionSets, setExpansionSets] = useState([]);
    const [contextReady, setContextReady] = useState(false);
    const [recentMatch, setRecentMatch] = useState(null);
    const [confirmDeleteMatchesOpen, setConfirmDeleteMatchesOpen] = useState(false);
    const [bulkTryhardUpdating, setBulkTryhardUpdating] = useState(false);
    const [duelsSyncPendingTotal, setDuelsSyncPendingTotal] = useState(0);
    const [duelsSyncConnected, setDuelsSyncConnected] = useState(false);
    const [duelsSyncWizardOpen, setDuelsSyncWizardOpen] = useState(false);
    const [duelsSyncLastSyncedAt, setDuelsSyncLastSyncedAt] = useState(null);
    const [prefillDeckCards, setPrefillDeckCards] = useState(null);

    const trackerFilters = useMemo(() => ({
        format: selectedFormat,
        setId: selectedSetId || null
    }), [selectedFormat, selectedSetId]);
    const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
    const selectedSetName = useMemo(() => {
        const match = expansionSets.find((entry) => entry.id === selectedSetId);
        return match?.name || selectedSetId || TRACKER_DEFAULT_SET_ID;
    }, [expansionSets, selectedSetId]);

    const persistTrackerContext = useCallback((format, setId) => {
        const nextSetId = normalizeTrackerSetId(setId) || TRACKER_DEFAULT_SET_ID;
        try {
            window.localStorage.setItem(
                TRACKER_CONTEXT_STORAGE_KEY,
                JSON.stringify({ format, setId: nextSetId })
            );
        } catch (error) {
            console.warn('Nie udało się zapisać kontekstu trackera do localStorage:', error);
        }
        if (!currentUser) return;
        updateUserTrackerContext(currentUser.uid, {
            format,
            setId: nextSetId
        })
            .then(() => refreshUserData?.())
            .catch((error) => {
                console.warn('Nie udało się zapisać kontekstu trackera w profilu użytkownika:', error);
            });
    }, [currentUser, refreshUserData]);

    const handleFormatChange = (event) => {
        const nextFormat = event.target.value;
        setSelectedFormat(nextFormat);
        persistTrackerContext(nextFormat, selectedSetId);
    };

    const handleSetChange = (event) => {
        const nextSetId = normalizeTrackerSetId(event.target.value) || TRACKER_DEFAULT_SET_ID;
        setSelectedSetId(nextSetId);
        persistTrackerContext(selectedFormat, nextSetId);
    };

    useEffect(() => {
        if (authLoading || (currentUser && !userData)) {
            setContextReady(false);
            return;
        }

        let nextFormat = 'core_constructed';
        let nextSetId = TRACKER_DEFAULT_SET_ID;

        if (currentUser) {
            if (isValidTrackerFormat(userData?.trackerSelectedFormat)) {
                nextFormat = userData.trackerSelectedFormat;
            }
            nextSetId = normalizeTrackerSetId(userData?.trackerSelectedSetId) || TRACKER_DEFAULT_SET_ID;
        } else {
            try {
                const raw = window.localStorage.getItem(TRACKER_CONTEXT_STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (isValidTrackerFormat(parsed?.format)) {
                        nextFormat = parsed.format;
                    }
                    nextSetId = normalizeTrackerSetId(parsed?.setId) || nextSetId;
                }
            } catch (error) {
                console.warn('Nie udało się odczytać kontekstu trackera z localStorage:', error);
            }
        }

        setSelectedFormat(nextFormat);
        setSelectedSetId(nextSetId);
        setContextReady(true);
    }, [authLoading, currentUser, userData]);

    // Funkcja do pobierania statystyk użytkownika
    const fetchUserStats = useCallback(async () => {
        if (!contextReady) {
            return;
        }
        if (!currentUser) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const dashboardData = await getTrackerDashboardData(currentUser.uid, trackerFilters);
            const { matches, allDecks } = dashboardData;
            const sortedMatches = [...matches].sort((a, b) => {
                const dateA = new Date(a.timestamp || a.createdAt || 0).getTime();
                const dateB = new Date(b.timestamp || b.createdAt || 0).getTime();
                return dateB - dateA;
            });
            setRecentMatch(sortedMatches[0] || null);

            const counters = matches.reduce((acc, match) => {
                if (match.result === 'win') acc.wins += 1;
                else if (match.result === 'lose') acc.losses += 1;
                else if (match.result === 'draw') acc.draws += 1;
                return acc;
            }, { wins: 0, losses: 0, draws: 0 });
            const { wins, losses, draws } = counters;
            const totalMatches = matches.length;
            // Winrate obliczamy tylko z wygranych i przegranych (bez remisów)
            const winrate = (wins + losses) > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;

            setStats({
                totalMatches,
                totalDecks: allDecks.length,
                winrate,
                wins,
                losses,
                draws
            });
        } catch (error) {
            console.error('Błąd podczas pobierania statystyk:', error);
        } finally {
            setLoading(false);
        }
    }, [contextReady, currentUser, trackerFilters]);

    // Pobierz statystyki przy załadowaniu komponentu i zmianie użytkownika
    useEffect(() => {
        fetchUserStats();
    }, [fetchUserStats]);

    // Pobierz liczbę oczekujących DuelSync (badge na zakładce)
    const fetchDuelsSyncPending = useCallback(async () => {
        if (!currentUser) return;
        try {
            const s = await getDuelsSyncStatus();
            setDuelsSyncPendingTotal((s?.pendingCount || 0) + (s?.duplicateCount || 0));
            setDuelsSyncConnected(Boolean(s?.connected));
            // Użyj max(lastSyncedAt, newestImportedAt) jako punkt startowy dla "Tylko nowe"
            const ls = s?.lastSyncedAt ? new Date(s.lastSyncedAt).getTime() : 0;
            const ni = s?.newestImportedAt ? new Date(s.newestImportedAt).getTime() : 0;
            const best = Math.max(ls, ni);
            setDuelsSyncLastSyncedAt(best > 0 ? new Date(best).toISOString() : null);
        } catch {
            // non-critical, ignore
        }
    }, [currentUser]);

    useEffect(() => {
        fetchDuelsSyncPending();
    }, [fetchDuelsSyncPending]);

    useEffect(() => {
        const loadSets = async () => {
            try {
                const sets = await getExpansionSets();
                setExpansionSets(sets);
                if (!contextReady) return;
                const preferredSetId = getPreferredTrackerSetId(sets, selectedSetId);
                if (preferredSetId && preferredSetId !== selectedSetId) {
                    setSelectedSetId(preferredSetId);
                    persistTrackerContext(selectedFormat, preferredSetId);
                }
            } catch (error) {
                console.error('Błąd podczas pobierania setów:', error);
            }
        };
        loadSets();
    }, [contextReady, persistTrackerContext, selectedFormat, selectedSetId]);

    useEffect(() => {
        if (!contextReady) return;
        try {
            window.localStorage.setItem(
                TRACKER_CONTEXT_STORAGE_KEY,
                JSON.stringify({
                    format: selectedFormat,
                    setId: normalizeTrackerSetId(selectedSetId) || TRACKER_DEFAULT_SET_ID
                })
            );
        } catch (error) {
            console.warn('Nie udało się zapisać kontekstu trackera do localStorage:', error);
        }
    }, [contextReady, selectedFormat, selectedSetId]);

    const handleOpenQuickMatch = (deckId = null) => {
        setPreselectedDeckId(deckId);
        if (isMobile) {
            setShowMobileMatchForm(true);
        } else {
            setQuickMatchOpen(true);
        }
    };

    const handleCloseQuickMatch = () => {
        setQuickMatchOpen(false);
        setPreselectedDeckId(null);
        setEditMatch(null);
    };

    const handleEditMatch = (match) => {
        setEditMatch(match);
        setQuickMatchOpen(true);
    };

    const handleBackFromMobileMatch = () => {
        setShowMobileMatchForm(false);
        setPreselectedDeckId(null);
    };

    const handleOpenArchetypeDialog = () => {
        setArchetypeDialogOpen(true);
    };

    const handleCloseArchetypeDialog = () => {
        setArchetypeDialogOpen(false);
    };

    const handleOpenDeckDialog = () => {
        setDeckDialogOpen(true);
    };

    const handleCloseDeckDialog = () => {
        setDeckDialogOpen(false);
    };

    const handleAddDeckFromDuelsSync = (cardsRaw) => {
        setPrefillDeckCards(cardsRaw || null);
        setActiveSection('decks');
    };

    const handleSectionChange = (sectionId) => {
        setActiveSection(sectionId);
    };

    const handleToggleDeleteMode = () => {
        setDeleteMode(!deleteMode);
        setSelectedMatches(new Set());
    };

    const handleSelectMatch = (matchId) => {
        const newSelected = new Set(selectedMatches);
        if (newSelected.has(matchId)) {
            newSelected.delete(matchId);
        } else {
            newSelected.add(matchId);
        }
        setSelectedMatches(newSelected);
    };

    const handleDeleteSelectedMatches = async () => {
        if (selectedMatches.size === 0) return;

        try {
            // Usuń mecze z bazy danych
            await deleteMatches(Array.from(selectedMatches));

            // Po usunięciu, wyczyść wybór i wyłącz tryb usuwania
            setSelectedMatches(new Set());
            setDeleteMode(false);

            // Odśwież statystyki i listę meczów
            fetchUserStats();
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error('Błąd podczas usuwania meczy:', err);
        }
    };

    const handleMarkSelectedMatchesAsTryhard = async () => {
        if (selectedMatches.size === 0 || bulkTryhardUpdating) return;

        try {
            setBulkTryhardUpdating(true);
            await Promise.all(
                Array.from(selectedMatches).map((matchId) =>
                    updateMatch(matchId, { tryhard: true })
                )
            );

            setSelectedMatches(new Set());
            setDeleteMode(false);
            fetchUserStats();
            setRefreshTrigger((prev) => prev + 1);
        } catch (err) {
            console.error('Błąd podczas masowej edycji meczy (tryhard):', err);
        } finally {
            setBulkTryhardUpdating(false);
        }
    };

    // Funkcja wywoływana po dodaniu meczu
    const handleMatchAdded = () => {
        fetchUserStats();
        setRefreshTrigger(prev => prev + 1);
    };

    const sections = useMemo(() => [
        {
            id: 'overview',
            title: 'Przegląd',
            icon: <AssessmentIcon sx={{ fontSize: 24 }} />,
            description: 'Ogólne statystyki i podsumowanie'
        },
        {
            id: 'matches',
            title: 'Mecze',
            icon: <SportsEsportsIcon sx={{ fontSize: 24 }} />,
            description: 'Rejestrowanie i historia meczów'
        },
        {
            id: 'archetypes',
            title: 'Archetypy',
            icon: <PaletteIcon sx={{ fontSize: 24 }} />,
            description: 'Zarządzanie archetypami talii'
        },
        {
            id: 'decks',
            title: 'Talie',
            icon: <DeckIcon sx={{ fontSize: 24 }} />,
            description: 'Zarządzanie taliami'
        },
        {
            id: 'my-stats',
            title: 'Moje Statystyki',
            icon: <PersonIcon sx={{ fontSize: 24 }} />,
            description: 'Twoje osobiste statystyki i wydajność'
        },
        {
            id: 'analytics',
            title: 'Matchupy',
            icon: <BarChartIcon sx={{ fontSize: 24 }} />,
            description: 'Globalny winrate matrix archetypów'
        },
        {
            id: 'duelsync',
            title: 'duels.ink',
            icon: (
                <Badge
                    badgeContent={duelsSyncPendingTotal}
                    color="warning"
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}
                >
                    <SyncAltIcon sx={{ fontSize: 24 }} />
                </Badge>
            ),
            description: 'Import z duels.ink i weryfikacja meczy'
        }
    ], [duelsSyncPendingTotal]);

    const renderContent = () => {
        switch (activeSection) {
            case 'matches':
                return <MatchesList
                    deleteMode={deleteMode}
                    selectedMatches={selectedMatches}
                    onToggleDeleteMode={handleToggleDeleteMode}
                    onSelectMatch={handleSelectMatch}
                    onDeleteSelectedMatches={handleDeleteSelectedMatches}
                    onStatsUpdate={fetchUserStats}
                    onMatchAdded={handleMatchAdded}
                    onOpenQuickMatch={handleOpenQuickMatch}
                    onEditMatch={handleEditMatch}
                    refreshTrigger={refreshTrigger}
                    trackerFilters={trackerFilters}
                />;

            case 'archetypes':
                return <ArchetypeManager
                    onOpenDialog={handleOpenArchetypeDialog}
                    onCloseDialog={handleCloseArchetypeDialog}
                    dialogOpen={archetypeDialogOpen}
                    canManageArchetypes={isAdmin}
                    trackerFilters={trackerFilters}
                />;

            case 'decks':
                return <DeckManager
                    onOpenDialog={handleOpenDeckDialog}
                    onCloseDialog={handleCloseDeckDialog}
                    dialogOpen={deckDialogOpen}
                    onOpenQuickMatch={handleOpenQuickMatch}
                    onStatsUpdate={fetchUserStats}
                    trackerFilters={trackerFilters}
                    trackerSetLabel={selectedSetName}
                    prefillDeckCards={prefillDeckCards}
                    onPrefillConsumed={() => setPrefillDeckCards(null)}
                />;

            case 'my-stats':
                return <UserStatsPanel userData={userData} loading={loading} trackerFilters={trackerFilters} />;

            case 'analytics':
                return <StatisticsPanel trackerFilters={trackerFilters} />;

            case 'duelsync':
                return (
                    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                            duels.ink
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.75)', mb: 3 }}>
                            Weryfikacja gier zaimportowanych z duels.ink.
                        </Typography>
                        <DuelSyncPendingQueue
                            onStatsUpdate={() => { fetchUserStats(); fetchDuelsSyncPending(); }}
                            trackerFilters={trackerFilters}
                            onAddDeck={handleAddDeckFromDuelsSync}
                        />
                    </Box>
                );

            default:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
                            Przegląd
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.75)', mb: 2.5 }}>
                            Szybki dostęp do najważniejszych danych i akcji gracza.
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Card sx={{
                                    background: 'rgba(17, 24, 39, 0.78)',
                                    border: '1px solid rgba(76, 29, 149, 0.25)',
                                    borderRadius: '12px'
                                }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                                                    Mecze
                                                </Typography>
                                                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                                    {loading ? '...' : userData?.stats?.overall?.games || stats.totalMatches}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                                                    Talie
                                                </Typography>
                                                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                                    {loading ? '...' : stats.totalDecks}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                                                    Winrate
                                                </Typography>
                                                <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>
                                                    {loading ? '...' :
                                                        userData?.stats?.overall?.winrate ?
                                                            `${userData.stats.overall.winrate.toFixed(1)}%` :
                                                            stats.totalMatches > 0 ? `${stats.winrate}%` : '--'
                                                    }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Divider sx={{ my: 2, borderColor: 'rgba(76, 29, 149, 0.2)' }} />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenQuickMatch} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                                Dodaj mecz
                                            </Button>
                                            <Button variant="outlined" onClick={() => setActiveSection('decks')} sx={{ textTransform: 'none' }}>
                                                Przejdź do talii
                                            </Button>
                                            <Button variant="outlined" onClick={() => setActiveSection('analytics')} sx={{ textTransform: 'none' }}>
                                                Sprawdź matchupy
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{
                                    background: 'rgba(17, 24, 39, 0.78)',
                                    border: '1px solid rgba(76, 29, 149, 0.25)',
                                    borderRadius: '12px',
                                    height: '100%'
                                }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Typography variant="overline" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                                            Ostatni mecz
                                        </Typography>
                                        {recentMatch ? (
                                            <Box>
                                                <Typography variant="h6" sx={{ color: recentMatch.result === 'win' ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                                                    {recentMatch.result === 'win' ? 'Wygrana' : 'Przegrana'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.75)' }}>
                                                    {recentMatch.playDraw === 'play' ? 'Zaczynałeś' : 'Byłeś na draw'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.75)' }}>
                                                Brak meczów. Dodaj pierwszy wpis, aby zobaczyć podsumowanie.
                                            </Typography>
                                        )}
                                        <Button
                                            variant="text"
                                            onClick={() => setActiveSection('matches')}
                                            sx={{ mt: 1.5, color: 'rgba(196, 181, 253, 0.95)', textTransform: 'none', px: 0 }}
                                        >
                                            Zobacz historię meczów
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                );
        }
    };

    // Renderuj widok mobilny jeśli jest aktywny
    if (showMobileMatchForm) {
        return (
            <QuickMatchMobile
                onBack={handleBackFromMobileMatch}
                preselectedDeckId={preselectedDeckId}
                onMatchAdded={handleMatchAdded}
                trackerFilters={trackerFilters}
                onGoToMatches={() => {
                    setShowMobileMatchForm(false);
                    setActiveSection('matches');
                }}
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2,
                        p: 2,
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(17, 24, 39, 0.75)'
                    }}
                >
                    <Typography sx={{ color: 'rgba(196, 181, 253, 0.9)', fontWeight: 600 }}>
                        Format gry: {FORMAT_LABELS[selectedFormat] || selectedFormat} · {selectedSetName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Format</InputLabel>
                            <Select
                                value={selectedFormat}
                                onChange={handleFormatChange}
                                label="Format"
                                sx={{
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.8)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' }
                                }}
                            >
                                <MenuItem value="core_constructed">Core Constructed</MenuItem>
                                <MenuItem value="infinity">Infinity</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Set</InputLabel>
                            <Select
                                value={selectedSetId}
                                onChange={handleSetChange}
                                label="Set"
                                sx={{
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.8)' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' }
                                }}
                            >
                                {expansionSets.map((setItem) => (
                                    <MenuItem key={setItem.id} value={setItem.id}>
                                        {setItem.name || setItem.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenQuickMatch}
                            size={isSmallMobile ? 'small' : 'medium'}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {isSmallMobile ? '+ Mecz' : 'Dodaj Mecz'}
                        </Button>
                    </Box>
                </Box>
                <Card sx={{
                    mt: 1.5,
                    background: 'rgba(17, 24, 39, 0.75)',
                    border: '1px solid rgba(76, 29, 149, 0.25)',
                    borderRadius: '10px'
                }}>
                    <Box
                        sx={{
                            px: { xs: 1, sm: 2 },
                            py: 1,
                            display: 'flex',
                            gap: 1.25,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap'
                        }}
                    >
                        <Tabs
                            value={activeSection}
                            onChange={(_, value) => handleSectionChange(value)}
                            variant={isSmallMobile ? 'scrollable' : 'standard'}
                            scrollButtons={isSmallMobile ? 'auto' : false}
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: 42,
                                flex: '1 1 auto',
                                minWidth: 0,
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#7c3aed'
                                }
                            }}
                        >
                            {sections.map((section) => (
                                <Tab
                                    key={section.id}
                                    value={section.id}
                                    icon={section.icon}
                                    iconPosition="start"
                                    label={section.title}
                                    sx={{
                                        minHeight: 42,
                                        minWidth: isSmallMobile ? 120 : 140,
                                        textTransform: 'none',
                                        color: 'rgba(196, 181, 253, 0.85)',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        '&.Mui-selected': {
                                            color: 'white',
                                            backgroundColor: 'rgba(124, 58, 237, 0.15)'
                                        }
                                    }}
                                />
                            ))}
                        </Tabs>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, ml: { xs: 0, sm: 'auto' } }}>
                            {duelsSyncConnected && (
                                <Tooltip title="Importuj najnowsze gry z duels.ink" arrow>
                                    <IconButton
                                        onClick={() => setDuelsSyncWizardOpen(true)}
                                        sx={{
                                            backgroundColor: 'rgba(124, 58, 237, 0.18)',
                                            color: 'rgba(196, 181, 253, 0.9)',
                                            border: '1px solid rgba(124, 58, 237, 0.4)',
                                            borderRadius: '8px',
                                            width: isSmallMobile ? 36 : 40,
                                            height: isSmallMobile ? 36 : 40,
                                            '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.35)' }
                                        }}
                                    >
                                        <SyncAltIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {activeSection === 'matches' && (
                                <>
                                    <Tooltip title={deleteMode ? 'Zakończ zaznaczanie' : 'Zaznacz mecze'}>
                                        <IconButton
                                            onClick={handleToggleDeleteMode}
                                            sx={{
                                                backgroundColor: deleteMode ? 'rgba(124, 58, 237, 0.3)' : 'rgba(124, 58, 237, 0.18)',
                                                color: deleteMode ? 'white' : 'rgba(196, 181, 253, 0.9)',
                                                border: '1px solid rgba(124, 58, 237, 0.4)',
                                                borderRadius: '8px',
                                                width: isSmallMobile ? 36 : 40,
                                                height: isSmallMobile ? 36 : 40,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(124, 58, 237, 0.35)'
                                                }
                                            }}
                                        >
                                            {deleteMode
                                                ? <CheckBoxIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                                                : <CheckBoxOutlinedIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                    {deleteMode && selectedMatches.size > 0 && (
                                        <Button
                                            variant="contained"
                                            size={isSmallMobile ? 'small' : 'medium'}
                                            onClick={handleMarkSelectedMatchesAsTryhard}
                                            disabled={bulkTryhardUpdating}
                                            startIcon={<LocalFireDepartmentIcon />}
                                            sx={{
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #ea580c, #c2410c)'
                                                },
                                                '&.Mui-disabled': {
                                                    color: 'rgba(255,255,255,0.7)',
                                                    background: 'rgba(249, 115, 22, 0.5)'
                                                }
                                            }}
                                        >
                                            {bulkTryhardUpdating ? 'Aktualizacja...' : `Tryhard (${selectedMatches.size})`}
                                        </Button>
                                    )}
                                    {deleteMode && selectedMatches.size > 0 && (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size={isSmallMobile ? 'small' : 'medium'}
                                            onClick={() => setConfirmDeleteMatchesOpen(true)}
                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Usuń ({selectedMatches.size})
                                        </Button>
                                    )}
                                </>
                            )}
                            {activeSection === 'decks' && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenDeckDialog}
                                    size={isSmallMobile ? 'small' : 'medium'}
                                    sx={{
                                        textTransform: 'none'
                                    }}
                                >
                                    Dodaj Talię
                                </Button>
                            )}
                            {activeSection === 'archetypes' && isAdmin && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenArchetypeDialog}
                                    size={isSmallMobile ? 'small' : 'medium'}
                                    sx={{
                                        textTransform: 'none'
                                    }}
                                >
                                    Dodaj Archetyp
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Card>

            </Box>

            {/* Content */}
            <Fade in={true} timeout={500}>
                <Box>
                    {renderContent()}
                </Box>
            </Fade>

            {/* Quick Match Form */}
            <QuickMatchForm
                open={quickMatchOpen}
                onClose={handleCloseQuickMatch}
                preselectedDeckId={preselectedDeckId}
                editMatch={editMatch}
                onMatchAdded={handleMatchAdded}
                trackerFilters={trackerFilters}
            />
            <ConfirmDialog
                open={confirmDeleteMatchesOpen}
                title="Usunąć zaznaczone mecze?"
                message={`Ta operacja usunie ${selectedMatches.size} ${selectedMatches.size === 1 ? 'mecz' : 'mecze'} i nie można jej cofnąć.`}
                confirmLabel="Usuń mecze"
                onClose={() => setConfirmDeleteMatchesOpen(false)}
                onConfirm={async () => {
                    await handleDeleteSelectedMatches();
                    setConfirmDeleteMatchesOpen(false);
                }}
            />

            <DuelSyncImportWizard
                open={duelsSyncWizardOpen}
                trackerFilters={trackerFilters}
                lastSyncedAt={duelsSyncLastSyncedAt}
                onClose={() => {
                    setDuelsSyncWizardOpen(false);
                    fetchUserStats();
                    fetchDuelsSyncPending();
                }}
            />
        </Container>
    );
}

export default Tracker;
